import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useNavigate } from "react-router-dom";

import { SessionContext } from "../../../../contexts";
import {
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../../common/native";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";

const TOKENS = ADMIN_TOKENS;
const TARGET_URL_OPTIONS = [
  "https://clinic-os.com/user-app-assets/stickers/wow.png",
  "https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/user-app-assets/stickers/wow.png",
];
const DOWNLOAD_FILENAME = "cors-debug-wow.png";
const TEST_TIMEOUT_MS = 12000;
const DEBUG_LOG_PREFIX = "[BA_CORS_DEBUG]";

type Status = "idle" | "success" | "failure";
type UrlTestMode = "image_cross_origin" | "fetch_cors" | "canvas_export";

type RequestBuildResult = {
  requestUrl: string;
  cacheBustApplied: boolean;
  cacheBustSkippedReason: string | null;
};

type StickerLoadResult = {
  status: Status;
  naturalWidth: number | null;
  naturalHeight: number | null;
  error: string | null;
  requestUrl: string | null;
  failedSrc: string | null;
  crossOrigin: string | null;
};

type FetchResult = {
  status: Status;
  ok: boolean | null;
  statusCode: number | null;
  error: string | null;
  requestUrl: string | null;
};

type CanvasExportResult = {
  status: Status;
  toDataUrlSuccess: boolean | null;
  toBlobSuccess: boolean | null;
  canvasWidth: number | null;
  canvasHeight: number | null;
  error: string | null;
  requestUrl: string | null;
};

type ErrorWithDetails = Error & {
  details?: Record<string, unknown>;
};

const defaultStickerLoadResult: StickerLoadResult = {
  status: "idle",
  naturalWidth: null,
  naturalHeight: null,
  error: null,
  requestUrl: null,
  failedSrc: null,
  crossOrigin: null,
};

const defaultFetchResult: FetchResult = {
  status: "idle",
  ok: null,
  statusCode: null,
  error: null,
  requestUrl: null,
};

const defaultCanvasExportResult: CanvasExportResult = {
  status: "idle",
  toDataUrlSuccess: null,
  toBlobSuccess: null,
  canvasWidth: null,
  canvasHeight: null,
  error: null,
  requestUrl: null,
};

const toErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) return error.message;
  return String(error);
};

const timestamp = () =>
  new Date().toLocaleString(undefined, {
    hour12: false,
    timeZoneName: "short",
  });

const summarizeUrlForLogs = (src?: string | null) => {
  if (!src) return src;
  try {
    const parsed = new URL(src);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return src;
  }
};

const createDetailedError = (
  message: string,
  details: Record<string, unknown>,
) => {
  const error = new Error(message) as ErrorWithDetails;
  error.details = details;
  return error;
};

const extractErrorPayload = (error: unknown) => {
  if (error instanceof Error) {
    const details = (error as ErrorWithDetails).details;
    return {
      message: error.message || "Unknown error",
      details: details && typeof details === "object" ? details : {},
    };
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    return {
      message: String((error as { message: unknown }).message),
      details: {},
    };
  }

  return {
    message: toErrorMessage(error),
    details: {},
  };
};

const toJsonString = (value: unknown) => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const CorsDebugPage: React.FC = () => {
  const navigate = useNavigate();
  const session = useContext(SessionContext)?.session;
  const blobRef = useRef<Blob | null>(null);

  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [isCanvasLoading, setIsCanvasLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [loadResult, setLoadResult] = useState<StickerLoadResult>(
    defaultStickerLoadResult,
  );
  const [fetchResult, setFetchResult] =
    useState<FetchResult>(defaultFetchResult);
  const [canvasResult, setCanvasResult] = useState<CanvasExportResult>(
    defaultCanvasExportResult,
  );
  const [logs, setLogs] = useState<string[]>([]);
  const [selectedTargetUrl, setSelectedTargetUrl] = useState(
    TARGET_URL_OPTIONS[0],
  );
  const [targetUrlInput, setTargetUrlInput] = useState(TARGET_URL_OPTIONS[0]);
  const [isPresetModalVisible, setIsPresetModalVisible] = useState(false);
  const [useCacheBust, setUseCacheBust] = useState(false);

  const isAdmin = session?.role === "CLINIC_ADMIN";
  const canDownload =
    loadResult.status === "success" || fetchResult.status === "success";

  const statusColor = useMemo(
    () => ({
      idle: TOKENS.color.textSecondary,
      success: TOKENS.color.accentDark,
      failure: TOKENS.color.errorTextStrong,
    }),
    [],
  );

  const effectiveTargetUrl = targetUrlInput.trim() || selectedTargetUrl;

  const addLog = (message: string) => {
    setLogs((prev) => [
      `[${timestamp()}] ${DEBUG_LOG_PREFIX} ${message}`,
      ...prev,
    ]);
  };

  const addStructuredLog = (
    event: string,
    details: Record<string, unknown>,
  ) => {
    addLog(`${event}: ${toJsonString(details)}`);
  };

  useEffect(() => {
    blobRef.current = null;
  }, [effectiveTargetUrl, useCacheBust]);

  const buildRequestUrl = (rawUrl: string): RequestBuildResult => {
    const trimmedUrl = rawUrl.trim();

    if (!useCacheBust) {
      return {
        requestUrl: trimmedUrl,
        cacheBustApplied: false,
        cacheBustSkippedReason: "disabled",
      };
    }

    try {
      const baseUrl =
        typeof window !== "undefined" ? window.location.href : undefined;
      const parsedUrl = baseUrl
        ? new URL(trimmedUrl, baseUrl)
        : new URL(trimmedUrl);

      const hasSignedQueryParams = Array.from(
        parsedUrl.searchParams.keys(),
      ).some((key) => key.toLowerCase().startsWith("x-amz-"));

      if (hasSignedQueryParams) {
        return {
          requestUrl: parsedUrl.toString(),
          cacheBustApplied: false,
          cacheBustSkippedReason: "signed_query_params",
        };
      }

      parsedUrl.searchParams.set("cors-debug-ts", String(Date.now()));
      return {
        requestUrl: parsedUrl.toString(),
        cacheBustApplied: true,
        cacheBustSkippedReason: null,
      };
    } catch {
      return {
        requestUrl: trimmedUrl,
        cacheBustApplied: false,
        cacheBustSkippedReason: "invalid_url_or_parse_failed",
      };
    }
  };

  const loadImageWithDiagnostics = async (
    targetUrl: string,
    mode: UrlTestMode,
  ): Promise<{
    image: HTMLImageElement;
    requestMeta: RequestBuildResult;
    details: Record<string, unknown>;
  }> =>
    new Promise((resolve, reject) => {
      if (typeof window === "undefined") {
        reject(
          createDetailedError("Image load test only runs in browser context.", {
            mode,
            timestampIso: new Date().toISOString(),
          }),
        );
        return;
      }

      const image = new window.Image();
      const requestMeta = buildRequestUrl(targetUrl);
      let settled = false;

      const baseDetails = {
        mode,
        timestampIso: new Date().toISOString(),
        targetUrl,
        requestUrl: requestMeta.requestUrl,
        requestUrlSummary: summarizeUrlForLogs(requestMeta.requestUrl),
        cacheBustEnabled: useCacheBust,
        cacheBustApplied: requestMeta.cacheBustApplied,
        cacheBustSkippedReason: requestMeta.cacheBustSkippedReason,
        crossOrigin: "anonymous",
      };

      const finalize = (
        handler: (
          resolveFn: (value: {
            image: HTMLImageElement;
            requestMeta: RequestBuildResult;
            details: Record<string, unknown>;
          }) => void,
          rejectFn: (reason?: unknown) => void,
        ) => void,
      ) => {
        if (settled) return;
        settled = true;
        image.onload = null;
        image.onerror = null;
        clearTimeout(timeoutId);
        handler(resolve, reject);
      };

      const timeoutId = setTimeout(() => {
        finalize((_, rejectFn) => {
          rejectFn(
            createDetailedError("Image load timed out.", {
              ...baseDetails,
              timeoutMs: TEST_TIMEOUT_MS,
              currentSrc: image.currentSrc || requestMeta.requestUrl,
              complete: image.complete,
              naturalWidth: image.naturalWidth,
              naturalHeight: image.naturalHeight,
            }),
          );
        });
      }, TEST_TIMEOUT_MS);

      image.crossOrigin = "anonymous";
      image.onload = () => {
        finalize((resolveFn) => {
          resolveFn({
            image,
            requestMeta,
            details: {
              ...baseDetails,
              currentSrc: image.currentSrc || requestMeta.requestUrl,
              complete: image.complete,
              naturalWidth: image.naturalWidth,
              naturalHeight: image.naturalHeight,
            },
          });
        });
      };

      image.onerror = (event) => {
        finalize((_, rejectFn) => {
          rejectFn(
            createDetailedError("Image load failed.", {
              ...baseDetails,
              eventType:
                typeof event === "string" ? event : (event?.type ?? "error"),
              failedSrc: image.currentSrc || requestMeta.requestUrl,
              complete: image.complete,
              naturalWidth: image.naturalWidth,
              naturalHeight: image.naturalHeight,
            }),
          );
        });
      };

      image.src = requestMeta.requestUrl;
    });

  const handleTestStickerLoad = async () => {
    if (isImageLoading) return;
    setIsImageLoading(true);
    addLog(`Test Image(crossOrigin) started for ${effectiveTargetUrl}`);

    try {
      const { image, requestMeta, details } = await loadImageWithDiagnostics(
        effectiveTargetUrl,
        "image_cross_origin",
      );

      setLoadResult({
        status: "success",
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
        error: null,
        requestUrl: requestMeta.requestUrl,
        failedSrc: null,
        crossOrigin: image.crossOrigin,
      });

      addStructuredLog("image_cross_origin_success", {
        ...details,
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
      });
    } catch (error) {
      const payload = extractErrorPayload(error);
      const failedSrc =
        typeof payload.details.failedSrc === "string"
          ? payload.details.failedSrc
          : null;
      const requestUrl =
        typeof payload.details.requestUrl === "string"
          ? payload.details.requestUrl
          : effectiveTargetUrl;
      const crossOrigin =
        typeof payload.details.crossOrigin === "string"
          ? payload.details.crossOrigin
          : "anonymous";

      setLoadResult({
        status: "failure",
        naturalWidth: null,
        naturalHeight: null,
        error: payload.message,
        requestUrl,
        failedSrc,
        crossOrigin,
      });

      addStructuredLog("image_cross_origin_failure", {
        message: payload.message,
        ...payload.details,
      });
    } finally {
      setIsImageLoading(false);
    }
  };

  const fetchCorsImage = async (targetUrl: string) => {
    const requestMeta = buildRequestUrl(targetUrl);
    const response = await fetch(requestMeta.requestUrl, {
      mode: "cors",
      cache: useCacheBust ? "no-store" : "default",
    });

    if (response.ok) {
      blobRef.current = await response.blob();
    }

    return { response, requestMeta };
  };

  const handleTestFetch = async () => {
    if (isFetchLoading) return;
    setIsFetchLoading(true);
    addLog(`Test Fetch (CORS) started for ${effectiveTargetUrl}`);

    try {
      const { response, requestMeta } =
        await fetchCorsImage(effectiveTargetUrl);
      setFetchResult({
        status: response.ok ? "success" : "failure",
        ok: response.ok,
        statusCode: response.status,
        error: null,
        requestUrl: requestMeta.requestUrl,
      });

      addStructuredLog("fetch_cors_complete", {
        mode: "fetch_cors",
        timestampIso: new Date().toISOString(),
        targetUrl: effectiveTargetUrl,
        requestUrl: requestMeta.requestUrl,
        requestUrlSummary: summarizeUrlForLogs(requestMeta.requestUrl),
        cacheBustEnabled: useCacheBust,
        cacheBustApplied: requestMeta.cacheBustApplied,
        cacheBustSkippedReason: requestMeta.cacheBustSkippedReason,
        ok: response.ok,
        status: response.status,
        type: response.type,
      });
    } catch (error) {
      const message = toErrorMessage(error);
      const requestMeta = buildRequestUrl(effectiveTargetUrl);
      setFetchResult({
        status: "failure",
        ok: null,
        statusCode: null,
        error: message,
        requestUrl: requestMeta.requestUrl,
      });

      addStructuredLog("fetch_cors_failure", {
        mode: "fetch_cors",
        timestampIso: new Date().toISOString(),
        targetUrl: effectiveTargetUrl,
        requestUrl: requestMeta.requestUrl,
        requestUrlSummary: summarizeUrlForLogs(requestMeta.requestUrl),
        cacheBustEnabled: useCacheBust,
        cacheBustApplied: requestMeta.cacheBustApplied,
        cacheBustSkippedReason: requestMeta.cacheBustSkippedReason,
        error: message,
      });
    } finally {
      setIsFetchLoading(false);
    }
  };

  const handleTestCanvasExport = async () => {
    if (isCanvasLoading) return;
    setIsCanvasLoading(true);
    addLog(`Test Canvas Export started for ${effectiveTargetUrl}`);

    try {
      if (typeof document === "undefined") {
        throw createDetailedError(
          "Canvas export test only runs in browser context.",
          {
            mode: "canvas_export",
            timestampIso: new Date().toISOString(),
          },
        );
      }

      const { image, requestMeta, details } = await loadImageWithDiagnostics(
        effectiveTargetUrl,
        "canvas_export",
      );

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, image.naturalWidth);
      canvas.height = Math.max(1, image.naturalHeight);

      const context = canvas.getContext("2d");
      if (!context) {
        throw createDetailedError("Unable to acquire 2D canvas context.", {
          ...details,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
        });
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      let toDataUrlSuccess = false;
      let toBlobSuccess = false;
      const exportErrors: string[] = [];

      try {
        const dataUrl = canvas.toDataURL("image/png");
        toDataUrlSuccess = dataUrl.startsWith("data:image/png");
        if (!toDataUrlSuccess) {
          exportErrors.push("toDataURL returned a non-PNG data URL.");
        }
      } catch (error) {
        exportErrors.push(`toDataURL error: ${toErrorMessage(error)}`);
      }

      try {
        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((nextBlob) => resolve(nextBlob), "image/png");
        });

        if (blob && blob.size > 0) {
          toBlobSuccess = true;
        } else {
          exportErrors.push("toBlob returned null or empty blob.");
        }
      } catch (error) {
        exportErrors.push(`toBlob error: ${toErrorMessage(error)}`);
      }

      const status = toDataUrlSuccess && toBlobSuccess ? "success" : "failure";

      setCanvasResult({
        status,
        toDataUrlSuccess,
        toBlobSuccess,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        error: exportErrors.length > 0 ? exportErrors.join(" | ") : null,
        requestUrl: requestMeta.requestUrl,
      });

      addStructuredLog(
        status === "success"
          ? "canvas_export_success"
          : "canvas_export_failure",
        {
          ...details,
          requestUrl: requestMeta.requestUrl,
          requestUrlSummary: summarizeUrlForLogs(requestMeta.requestUrl),
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          toDataUrlSuccess,
          toBlobSuccess,
          exportErrors,
        },
      );
    } catch (error) {
      const payload = extractErrorPayload(error);
      const requestUrl =
        typeof payload.details.requestUrl === "string"
          ? payload.details.requestUrl
          : effectiveTargetUrl;

      setCanvasResult({
        status: "failure",
        toDataUrlSuccess: null,
        toBlobSuccess: null,
        canvasWidth: null,
        canvasHeight: null,
        error: payload.message,
        requestUrl,
      });

      addStructuredLog("canvas_export_failure", {
        message: payload.message,
        ...payload.details,
      });
    } finally {
      setIsCanvasLoading(false);
    }
  };

  const handleTargetUrlSelect = (value: string) => {
    setSelectedTargetUrl(value);
    setTargetUrlInput(value);
    setLoadResult(defaultStickerLoadResult);
    setFetchResult(defaultFetchResult);
    setCanvasResult(defaultCanvasExportResult);
    addLog(`Target URL preset selected: ${value}`);
  };

  const handleTargetUrlChange = (value: string) => {
    setTargetUrlInput(value);
  };

  const handleToggleCacheBust = () => {
    const next = !useCacheBust;
    setUseCacheBust(next);
    setLoadResult(defaultStickerLoadResult);
    setFetchResult(defaultFetchResult);
    setCanvasResult(defaultCanvasExportResult);
    addLog(`Cache busting ${next ? "enabled" : "disabled"}.`);
  };

  const handleDownload = async () => {
    if (!canDownload || isDownloadLoading) return;
    setIsDownloadLoading(true);
    addLog(`Download Test Image started (${DOWNLOAD_FILENAME})`);

    try {
      if (typeof document === "undefined") {
        throw new Error("Download test only runs in browser context.");
      }

      let blob = blobRef.current;
      if (!blob) {
        addLog("No cached blob found; fetching image blob for download.");
        const { response, requestMeta } =
          await fetchCorsImage(effectiveTargetUrl);
        if (!response.ok || !blobRef.current) {
          throw new Error(
            `Unable to fetch image blob (status=${response.status}, url=${requestMeta.requestUrl}).`,
          );
        }
        blob = blobRef.current;
      }

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = DOWNLOAD_FILENAME;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      addLog(`Download Test Image success (${DOWNLOAD_FILENAME})`);
    } catch (error) {
      addLog(`Download Test Image failure (${toErrorMessage(error)})`);
    } finally {
      setIsDownloadLoading(false);
    }
  };

  if (!session) {
    return (
      <View style={styles.accessDeniedContainer}>
        <TitleText text="CORS Debug" />
        <Text style={styles.accessDeniedText}>Loading session...</Text>
      </View>
    );
  }

  if (!isAdmin) {
    return (
      <View style={styles.accessDeniedContainer}>
        <TitleText text="CORS Debug" />
        <Text style={styles.accessDeniedText}>
          This page is restricted to clinic admins.
        </Text>
        <SecondaryButton
          text="Back to Home"
          onPress={() => navigate("/home")}
          style={styles.backButton}
        />
      </View>
    );
  }

  return (
    <>
      <ScrollView
        style={styles.page}
        contentContainerStyle={styles.pageContent}
      >
        <View style={styles.header}>
          <TitleText text="CORS Debug" />
          <Text style={styles.subtitle}>
            Internal diagnostics for export CORS behavior with fetch(cors),
            Image(crossOrigin), and canvas export.
          </Text>
          <Text style={styles.metaText}>Target URL: {effectiveTargetUrl}</Text>
        </View>

        <View style={styles.actionSection}>
          <View style={styles.targetUrlSection}>
            <Text style={styles.inputLabel}>URL Preset</Text>
            <SecondaryButton
              text="Choose URL Preset"
              onPress={() => setIsPresetModalVisible(true)}
            />
            <Text style={styles.selectedPresetText}>
              Selected preset: {selectedTargetUrl}
            </Text>

            <Text style={styles.inputLabel}>Target URL (editable)</Text>
            <TextInput
              value={targetUrlInput}
              onChangeText={handleTargetUrlChange}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder="https://..."
              style={styles.targetUrlInput}
              accessibilityLabel="Target URL"
            />

            <SecondaryButton
              text={useCacheBust ? "Cache Busting: On" : "Cache Busting: Off"}
              onPress={handleToggleCacheBust}
            />
            <Text style={styles.helperText}>
              Cache busting is optional and skipped when signed query params
              (for example `X-Amz-*`) are detected.
            </Text>
          </View>

          <PrimaryButton
            text={
              isImageLoading
                ? "Testing Image(crossOrigin)..."
                : "Test Image(crossOrigin)"
            }
            onPress={handleTestStickerLoad}
            disabled={
              isImageLoading ||
              isFetchLoading ||
              isCanvasLoading ||
              isDownloadLoading
            }
          />
          <PrimaryButton
            text={isFetchLoading ? "Testing Fetch..." : "Test Fetch (CORS)"}
            onPress={handleTestFetch}
            disabled={
              isImageLoading ||
              isFetchLoading ||
              isCanvasLoading ||
              isDownloadLoading
            }
          />
          <PrimaryButton
            text={
              isCanvasLoading
                ? "Testing Canvas Export..."
                : "Test Canvas Export"
            }
            onPress={handleTestCanvasExport}
            disabled={
              isImageLoading ||
              isFetchLoading ||
              isCanvasLoading ||
              isDownloadLoading
            }
          />
          <SecondaryButton
            text={isDownloadLoading ? "Downloading..." : "Download Test Image"}
            onPress={handleDownload}
            disabled={
              !canDownload ||
              isImageLoading ||
              isFetchLoading ||
              isCanvasLoading
            }
          />
        </View>

        <View style={styles.resultsSection}>
          <Text style={styles.sectionTitle}>Latest Results</Text>

          <View style={styles.resultCard}>
            <Text style={styles.resultTitle}>Image (crossOrigin)</Text>
            <Text
              style={[
                styles.resultStatus,
                { color: statusColor[loadResult.status] },
              ]}
            >
              {loadResult.status}
            </Text>
            <Text style={styles.resultDetail}>
              naturalWidth: {loadResult.naturalWidth ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              naturalHeight: {loadResult.naturalHeight ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              requestUrl: {loadResult.requestUrl ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              failedSrc: {loadResult.failedSrc ?? "none"}
            </Text>
            <Text style={styles.resultDetail}>
              crossOrigin: {loadResult.crossOrigin ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              error: {loadResult.error ?? "none"}
            </Text>
          </View>

          <View style={styles.resultCard}>
            <Text style={styles.resultTitle}>Fetch (CORS)</Text>
            <Text
              style={[
                styles.resultStatus,
                { color: statusColor[fetchResult.status] },
              ]}
            >
              {fetchResult.status}
            </Text>
            <Text style={styles.resultDetail}>
              ok: {fetchResult.ok === null ? "n/a" : String(fetchResult.ok)}
            </Text>
            <Text style={styles.resultDetail}>
              status: {fetchResult.statusCode ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              requestUrl: {fetchResult.requestUrl ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              error: {fetchResult.error ?? "none"}
            </Text>
          </View>

          <View style={styles.resultCard}>
            <Text style={styles.resultTitle}>Canvas Export</Text>
            <Text
              style={[
                styles.resultStatus,
                { color: statusColor[canvasResult.status] },
              ]}
            >
              {canvasResult.status}
            </Text>
            <Text style={styles.resultDetail}>
              toDataURL:{" "}
              {canvasResult.toDataUrlSuccess === null
                ? "n/a"
                : String(canvasResult.toDataUrlSuccess)}
            </Text>
            <Text style={styles.resultDetail}>
              toBlob:{" "}
              {canvasResult.toBlobSuccess === null
                ? "n/a"
                : String(canvasResult.toBlobSuccess)}
            </Text>
            <Text style={styles.resultDetail}>
              canvasWidth: {canvasResult.canvasWidth ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              canvasHeight: {canvasResult.canvasHeight ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              requestUrl: {canvasResult.requestUrl ?? "n/a"}
            </Text>
            <Text style={styles.resultDetail}>
              error: {canvasResult.error ?? "none"}
            </Text>
          </View>
        </View>

        <View style={styles.logsSection}>
          <View style={styles.logHeader}>
            <Text style={styles.sectionTitle}>Attempt Logs</Text>
            <SecondaryButton text="Clear Logs" onPress={() => setLogs([])} />
          </View>
          <ScrollView
            style={styles.logsContainer}
            contentContainerStyle={styles.logsContent}
          >
            {logs.length === 0 ? (
              <Text style={styles.emptyLogText}>No attempts yet.</Text>
            ) : (
              logs.map((log, index) => (
                <Text style={styles.logLine} key={`${log}-${index}`}>
                  {log}
                </Text>
              ))
            )}
          </ScrollView>
        </View>

        <View style={styles.footerActions}>
          <SecondaryButton
            text="Back to Admin Management"
            onPress={() => navigate("/admin-management")}
          />
        </View>
      </ScrollView>

      <Modal
        transparent
        animationType="fade"
        visible={isPresetModalVisible}
        onRequestClose={() => setIsPresetModalVisible(false)}
      >
        <Pressable
          style={styles.modalBackdrop}
          onPress={() => setIsPresetModalVisible(false)}
        >
          <Pressable style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>Choose URL Preset</Text>
            <ScrollView contentContainerStyle={styles.modalList}>
              {TARGET_URL_OPTIONS.map((option) => {
                const selected = option === selectedTargetUrl;
                return (
                  <Pressable
                    key={option}
                    style={[
                      styles.modalOption,
                      selected && styles.modalOptionSelected,
                    ]}
                    onPress={() => {
                      handleTargetUrlSelect(option);
                      setIsPresetModalVisible(false);
                    }}
                  >
                    <Text
                      style={[
                        styles.modalOptionText,
                        selected && styles.modalOptionTextSelected,
                      ]}
                    >
                      {option}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
            <SecondaryButton
              text="Close"
              onPress={() => setIsPresetModalVisible(false)}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: TOKENS.color.background,
  },
  pageContent: {
    padding: TOKENS.spacing.xxl,
    gap: TOKENS.spacing.lg,
    width: "100%",
    maxWidth: TOKENS.size.contentMaxWidth,
    alignSelf: "center",
  },
  header: {
    gap: TOKENS.spacing.sm,
  },
  subtitle: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textSecondary,
    lineHeight: TOKENS.lineHeight.md,
  },
  metaText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSubtle,
    lineHeight: TOKENS.lineHeight.sm,
  },
  actionSection: {
    backgroundColor: TOKENS.color.surface,
    borderColor: TOKENS.color.border,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.xl,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.sm,
  },
  targetUrlSection: {
    gap: TOKENS.spacing.sm,
  },
  inputLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  selectedPresetText: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
    lineHeight: TOKENS.lineHeight.sm,
  },
  targetUrlInput: {
    minHeight: 46,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderInput,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    backgroundColor: TOKENS.color.surface,
  },
  helperText: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSubtle,
    lineHeight: TOKENS.lineHeight.xs,
  },
  resultsSection: {
    gap: TOKENS.spacing.sm,
  },
  sectionTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.lg,
    color: TOKENS.color.textPrimary,
  },
  resultCard: {
    backgroundColor: TOKENS.color.surface,
    borderColor: TOKENS.color.border,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  resultTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  resultStatus: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.sm,
    textTransform: "uppercase",
  },
  resultDetail: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
    lineHeight: TOKENS.lineHeight.sm,
  },
  logsSection: {
    gap: TOKENS.spacing.sm,
  },
  logHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: TOKENS.spacing.sm,
  },
  logsContainer: {
    backgroundColor: TOKENS.color.surface,
    borderColor: TOKENS.color.border,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.lg,
    minHeight: TOKENS.size.chartHeight,
    maxHeight: TOKENS.size.chartHeight * 2,
  },
  logsContent: {
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  emptyLogText: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSubtle,
    lineHeight: TOKENS.lineHeight.sm,
  },
  logLine: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    lineHeight: TOKENS.lineHeight.sm,
  },
  footerActions: {
    paddingBottom: TOKENS.spacing.lg,
  },
  accessDeniedContainer: {
    flex: 1,
    backgroundColor: TOKENS.color.background,
    justifyContent: "center",
    alignItems: "center",
    padding: TOKENS.spacing.xxl,
    gap: TOKENS.spacing.sm,
  },
  accessDeniedText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textSecondary,
    textAlign: "center",
  },
  backButton: {
    minWidth: TOKENS.size.quickButtonMinWidth,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(17, 24, 39, 0.55)",
    justifyContent: "center",
    alignItems: "center",
    padding: TOKENS.spacing.lg,
  },
  modalCard: {
    width: "100%",
    maxWidth: TOKENS.size.contentMaxWidth,
    maxHeight: "80%",
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.xl,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.md,
  },
  modalTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.lg,
    color: TOKENS.color.textPrimary,
  },
  modalList: {
    gap: TOKENS.spacing.sm,
  },
  modalOption: {
    minHeight: 44,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderInput,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    justifyContent: "center",
    backgroundColor: TOKENS.color.surface,
  },
  modalOptionSelected: {
    borderColor: TOKENS.color.accent,
    backgroundColor: TOKENS.color.surfaceAccent,
  },
  modalOptionText: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    lineHeight: TOKENS.lineHeight.sm,
  },
  modalOptionTextSelected: {
    fontFamily: TOKENS.font.semibold,
    color: TOKENS.color.accentDark,
  },
});

export default CorsDebugPage;
